import { useEffect, useId, useRef, useState } from 'react';
import { ClipboardList, Inbox, LockKeyhole, PencilLine } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { CoachModal } from '../components/CoachModal';
import { DraftInbox } from '@/features/draft-inbox/DraftInbox';
import { draftContextKey, isCurrentDraftContext, useComposerDraftContext } from '@/features/coach-review/composerDrafts';
import { useCanonicalCoachWorkspace } from '@/features/feedback-integration/CanonicalCoachWorkspaceHost';
import type { SavedPrivateFeedbackRequest } from '@/features/feedback-integration/resolveSavedPrivateFeedback';
import type { SavedPublicFeedbackRequest } from '@/features/feedback-integration/resolvePublishedAssistFeedback';
import type { FeedbackFocusRequest } from '@/features/feedback-integration/resolveFeedbackFocus';
import type { PlayerGoal } from '@/lib/types';
import { AssistWorkspace } from './CoachAssistPanel';
import './assist-sheet.css';

type AssistTask = 'choose' | 'capture' | 'prepare' | 'drafts';
export type CoachAssistSheetProps = {
  open: boolean;
  onClose: () => void;
  onManualFeedback: () => void;
  captureId?: string | null;
  returnFocusTo?: HTMLElement | null;
  onOpenPlayer?: (playerId: string, name: string) => void;
  onNewGoal?: (playerId: string) => void;
  onEditGoal?: (goal: PlayerGoal) => void;
};

/** First open mounts the owned workspace; ordinary close only hides it. */
export function CoachAssistSheet(props: CoachAssistSheetProps) {
  const owner = useComposerDraftContext();
  const [opened, setOpened] = useState(props.open);
  useEffect(() => { if (props.open) setOpened(true); }, [props.open]);
  if (!opened || !owner) return null;
  return <OwnedAssistSheet key={draftContextKey(owner)} {...props} owner={owner} />;
}

function OwnedAssistSheet({ open, onClose, onManualFeedback, captureId, returnFocusTo, owner, ...callbacks }: CoachAssistSheetProps & { owner: NonNullable<ReturnType<typeof useComposerDraftContext>> }) {
  const id = useId();
  const workspace = useCanonicalCoachWorkspace();
  const opening = useRef<AbortController | null>(null);
  const [task, setTask] = useState<AssistTask>(captureId ? 'drafts' : 'choose');
  const [requestedCapture, setRequestedCapture] = useState(captureId ?? null);
  const [selectionIntent, setSelectionIntent] = useState(0);
  useEffect(() => {
    if (!open) opening.current?.abort();
    return () => { opening.current?.abort(); };
  }, [open, task]);
  async function openSavedFeedback(request: SavedPrivateFeedbackRequest | SavedPublicFeedbackRequest | FeedbackFocusRequest, signal: AbortSignal) {
    opening.current?.abort();
    const taskAllowed = 'feedbackId' in request ? task === 'prepare' || task === 'capture' : task === 'drafts';
    if (signal.aborted || !open || !taskAllowed || draftContextKey(request.owner) !== draftContextKey(owner)
        || !isCurrentDraftContext(request.owner) || !workspace?.authoring.current)
      throw new Error('The saved Feedback host is unavailable in this context.');
    const abort = new AbortController(); opening.current = abort;
    const cancel = () => abort.abort();
    signal.addEventListener('abort', cancel, { once: true });
    try {
      const beforeOpen = () => {
        // This is the acknowledged host handoff, not a user cancellation.
        if (opening.current === abort) opening.current = null;
        signal.removeEventListener('abort', cancel);
        onClose();
      };
      if ('feedbackId' in request) {
        await workspace.authoring.current.openFeedbackFocus(request, abort.signal, beforeOpen);
      } else if (request.accepted.schema_version === 'coaching-feedback-accepted.v1')
        await workspace.authoring.current.openPublishedAssist({ owner: request.owner, accepted: request.accepted }, abort.signal, beforeOpen);
      else await workspace.authoring.current.openSavedPrivate({ owner: request.owner, accepted: request.accepted }, abort.signal, beforeOpen);
    } finally {
      signal.removeEventListener('abort', cancel);
      if (opening.current === abort) opening.current = null;
    }
  }
  useEffect(() => {
    if (captureId) { setRequestedCapture(captureId); setTask('drafts'); }
  }, [captureId]);
  const tabs = [
    { key: 'capture', label: 'Capture', icon: PencilLine },
    { key: 'prepare', label: 'Prepare', icon: ClipboardList },
    { key: 'drafts', label: 'Private drafts', icon: Inbox },
  ] as const;
  return <CoachModal title="Tktic Assist" description="Private until you approve sharing." size="sheet" active={open} onClose={onClose} returnFocusTo={returnFocusTo} bodyClassName="assist-sheet-body" data-testid="coach-assist-sheet" footer={<div className="flex flex-wrap items-center justify-between gap-2"><span className="flex items-center gap-2 text-xs text-ink-muted"><LockKeyhole aria-hidden="true" className="size-4" />Private workspace</span><Button type="button" variant="outline" onClick={onClose}>Back to Board</Button></div>}>
    <div hidden={task === 'choose'} className="assist-task-tabs" role="tablist" aria-label="Tktic Assist tasks">
      {tabs.map(({ key, label, icon: Icon }, index) => <button key={key} type="button" role="tab" id={`${id}-${key}-tab`} aria-controls={`${id}-${key}-panel`} aria-selected={task === key} tabIndex={task === key ? 0 : -1} onClick={() => setTask(key)} onKeyDown={event => {
        const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length : event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : null;
        if (next === null) return;
        event.preventDefault(); setTask(tabs[next].key); document.getElementById(`${id}-${tabs[next].key}-tab`)?.focus();
      }}><Icon aria-hidden="true" className="size-4" />{label}</button>)}
    </div>
    {task === 'choose' && <section className="assist-sheet-task space-y-4" aria-label="Choose a coaching task">
      <h3 className="font-display text-lg text-ink">What do you want to do?</h3>
      <Button type="button" variant="outline" aria-label="Write player feedback" className="w-full h-auto justify-start whitespace-normal text-left py-4" onClick={() => setTask('capture')}>
        <span><strong className="block">Write player feedback</strong><span className="block mt-1 font-normal text-sm">Start with what you noticed. AI helps draft the message.</span></span>
      </Button>
      <Button type="button" variant="outline" aria-label="Prepare a player brief" className="w-full h-auto justify-start whitespace-normal text-left py-4" onClick={() => setTask('prepare')}>
        <span><strong className="block">Prepare a player brief</strong><span className="block mt-1 font-normal text-sm">Find a coaching focus from saved history.</span></span>
      </Button>
      <div className="flex flex-wrap gap-2"><Button type="button" variant="ghost" onClick={() => setTask('drafts')}>Saved drafts</Button><Button type="button" variant="ghost" onClick={onManualFeedback}>Write feedback manually</Button></div>
    </section>}
    <div className="assist-sheet-task" hidden={task === 'drafts' || task === 'choose'} id={`${id}-${task === 'prepare' ? 'prepare' : 'capture'}-panel`} role="tabpanel" aria-labelledby={`${id}-${task === 'prepare' ? 'prepare' : 'capture'}-tab`}>
      <AssistWorkspace owner={owner} visible={open && task !== 'drafts' && task !== 'choose'} task={task === 'prepare' ? 'prepare' : 'capture'} {...callbacks} onEditFeedback={workspace ? openSavedFeedback : undefined} onReviewCapture={id => { setRequestedCapture(id); setSelectionIntent(previous => previous + 1); setTask('drafts'); }} />
    </div>
    <div hidden={task !== 'drafts'} id={`${id}-drafts-panel`} role="tabpanel" aria-labelledby={`${id}-drafts-tab`} className="assist-sheet-task">
      <h3 className="font-display text-lg text-ink">Saved drafts</h3>
      <p className="mt-1 mb-4 text-sm text-ink-muted">Choose a capture to continue.</p>
      <DraftInbox embedded visible={open && task === 'drafts'} captureId={requestedCapture} selectionIntent={selectionIntent}
        onManualFeedback={onManualFeedback} {...{
          onOpenSavedFeedback: workspace ? openSavedFeedback : undefined,
          onOpenPublishedFeedback: workspace ? openSavedFeedback : undefined,
        }} />
    </div>
  </CoachModal>;
}
